import { useRef, useState } from 'react';
import { Canvas } from 'fabric';

import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import SubActivityView from '../sub-activity/sub-activity-view';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import {
  PlayableComponentButton,
  PlayableComponentButtonText,
} from '../shared/playable-component.style';
import { ZoomComponentTrigger } from 'approot/activity-builder/activity-builder-zoom-component-modal';
import { ZoomComponentModalProps } from 'approot/activity-builder/activity-builder-zoom-component-modal.constants';
import { useActivityAnswerId } from 'approot/activity-builder/present/activity-answers.hooks';
import { useStudentGetAnswers } from 'approot/activity-builder/student/answer/student-answer.hooks';
import { useBatchStudentAnswersContext } from '../shared/batch-student-answer.context';
import {
  ACTIVITIES_ANSWER_DRAW_TYPE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
} from 'global/constants';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { ViewProps } from './draw.constants';
import DrawCanvasPreview from './draw-canvas-preview';
import * as Styled from './draw-view.style';
import DrawCanvas from './canvas/draw-canvas';
import ActivitySheet from '../shared/sheet/activity-sheet';
import ActivitySheetSaveStatus from '../shared/sheet/activity-sheet-save-status';
import ComponentLoading from '../shared/component-loading';
import { useDrawLanguage } from './canvas/use-draw-language';
import { isTextbox } from './canvas/logic/is-textbox';

type Props = ViewProps & {
  isLoggedIn: boolean;
  onOpenDrawCanvas?: () => void;
} & ZoomComponentModalProps;

const DrawStudent = (props: Props) => {
  const { setActiveComponent } = useActivityBuilderPresentContext();

  return (
    <SubActivityView
      subActivityDetails={props.subActivityProperties}
      componentId={props.id}
      componentType={props.type}
      onClick={() => setActiveComponent({ id: props.id })}
    >
      {!props.isLoggedIn && props.openModal && props.properties.image && (
        <ZoomComponentTrigger
          ariaLabel={`Expand to view: ${props.properties.altText}`}
          openModal={props.openModal}
        />
      )}
      <DrawViewCanvas {...props} isLoggedIn={props.isLoggedIn} />
    </SubActivityView>
  );
};

const DrawViewCanvas = (
  props: Props & {
    isLoggedIn: boolean;
  }
) => {
  const [previewRef, setPreviewRef] = useState<HTMLDivElement | null>(null);
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const language = useDrawLanguage();
  const canvasRef = useRef<Canvas | null>(null);

  const { componentAnswers, variables, hasFetchedAnswers, error } =
    useStudentGetAnswers({
      componentId: props.id,
      skip: !props.isLoggedIn,
    });
  const answerData = componentAnswers?.at(0);
  const answerId = useActivityAnswerId(answerData);

  const { handleUpdate } = useBatchStudentAnswersContext();

  // Finalize any active textbox editing before closing to ensure text is saved
  const finalizeAndClose = () => {
    if (canvasRef.current) {
      const activeObject = canvasRef.current.getActiveObject();
      if (isTextbox(activeObject) && activeObject.isEditing) {
        activeObject.exitEditing();
        // Ensure canvas is re-rendered after exiting editing
        canvasRef.current.requestRenderAll();
      }
    }
    closeModal();
  };

  const paddingTop =
    (props.properties.dimensionData.height /
      props.properties.dimensionData.width) *
    100;

  return (
    <>
      <Styled.DrawCanvas
        style={{
          paddingTop: `${paddingTop}%`,
        }}
      >
        <Styled.DrawViewPreview
          ref={setPreviewRef}
          role="image"
          aria-label={props.properties.altText || ''}
        >
          {previewRef && (
            <DrawCanvasPreview
              id={props.id}
              dimensionsData={props.properties.dimensionData}
              imageUrl={props.properties.image?.original}
              // this key is to re-render on data updates could instead use an effect
              key={`${props.id}-${answerData?.uuid}-${answerData?.updatedAt}`}
              containerEl={previewRef}
              answer={(answerData?.answer as DrawAnswerFragment)?.drawing}
              questionData={props.properties.draw || undefined}
              language={language}
            />
          )}
        </Styled.DrawViewPreview>
        {props.isLoggedIn && (
          <>
            {!hasFetchedAnswers || error ? (
              <ComponentLoading hasData={hasFetchedAnswers} error={error} />
            ) : (
              <PlayableComponentButton
                type="button"
                data-testid="open-drawer-canvas-button"
                aria-label="Click to start drawing your answer"
                onClick={() => {
                  props.onOpenDrawCanvas?.();
                  openModal();
                }}
                data-selector="btn-open-drawer-canvas"
              >
                <PlayableComponentButtonText>
                  Add Answer
                </PlayableComponentButtonText>
              </PlayableComponentButton>
            )}
          </>
        )}
      </Styled.DrawCanvas>

      {isVisible && (
        <ActivitySheet
          isVisible
          title="Drawing response"
          customActions={
            <ActivitySheetSaveStatus
              title="Drawing response"
              onClose={finalizeAndClose}
            />
          }
        >
          <Styled.DrawViewStudent>
            <DrawCanvas
              id={props.id}
              type={props.type}
              imageUrl={props.properties.image?.original}
              dimensionsData={props.properties.dimensionData}
              subActivityProperties={props.subActivityProperties}
              questionData={props.properties.draw}
              language={language}
              answer={(answerData?.answer as DrawAnswerFragment)?.drawing}
              canvasRef={canvasRef}
              onSave={content => {
                if (
                  props.subActivityProperties?.activityComponentId &&
                  variables &&
                  props.isLoggedIn
                ) {
                  handleUpdate({
                    variables,
                    input: {
                      __typename: 'ActivityAnswer',
                      uuid: answerId,
                      answerType: ACTIVITIES_ANSWER_DRAW_TYPE,
                      activityId:
                        props.subActivityProperties.activityComponentId,
                      componentId: props.id,
                      createdAt: null,
                      updatedAt: new Date(),
                      studentId: null,
                      answer: {
                        __typename: 'DrawAnswer',
                        answerType: ACTIVITIES_ANSWER_DRAW_TYPE,
                        drawing: content,
                        hasDrawingData: content.data.objects.length > 0,
                      },
                      answerReferences: null,
                    },
                    dataTrackingComponentType: ACTIVITIES_COMPONENT_TYPE_DRAW,
                  });
                }
              }}
            />
          </Styled.DrawViewStudent>
        </ActivitySheet>
      )}
    </>
  );
};

export default DrawStudent;
